import { isChild } from '@apia/util';
import { TAttachToElement, TPosition, TTooltip } from './types';
import { ThemeUICSSObject } from '@apia/theme';
import React, { useCallback, useEffect, useRef } from 'react';

export function isAttachToElementAFunction(
  attachToElement: TAttachToElement,
): attachToElement is () => HTMLElement {
  return (
    attachToElement instanceof Function &&
    getParamNames(attachToElement).length === 0
  );
}

const STRIP_COMMENTS = /((\/\/.*$)|(\/\*[\s\S]*?\*\/))/gm;
const ARGUMENT_NAMES = /([^\s,]+)/g;

export function getParamNames(func: object) {
  // eslint-disable-next-line @typescript-eslint/no-base-to-string
  const fnStr = func.toString().replace(STRIP_COMMENTS, '');
  let result: string[] | RegExpMatchArray | null = fnStr
    .slice(fnStr.indexOf('(') + 1, fnStr.indexOf(')'))
    .match(ARGUMENT_NAMES);
  if (result === null) result = [];

  return result;
}

function determineAnchorPoint({
  attachToElement,
  attachToElementAnchorPoint,
}: Pick<TTooltip, 'attachToElementAnchorPoint'> &
  Required<Pick<TTooltip, 'attachToElement'>>) {
  let actualAnchorPoint: TPosition = { left: 0, top: 0 };

  const element = isAttachToElementAFunction(attachToElement)
    ? attachToElement()
    : attachToElement.current;

  if (element) {
    const rect = element.getBoundingClientRect();

    const point = attachToElementAnchorPoint ?? 'topRight';

    switch (point) {
      case 'bottomLeft':
        actualAnchorPoint = {
          left: rect.left,
          top: rect.bottom,
        };
        break;
      case 'bottomRight':
        actualAnchorPoint = {
          left: rect.right,
          top: rect.bottom,
        };
        break;
      case 'center':
        actualAnchorPoint = {
          left: (rect.left + rect.right) / 2,
          top: (rect.bottom + rect.top) / 2,
        };
        break;
      case 'topLeft':
        actualAnchorPoint = {
          left: rect.left,
          top: rect.top,
        };
        break;
      case 'topRight':
        actualAnchorPoint = {
          left: rect.right,
          top: rect.top,
        };
        break;
    }
  } else {
    console.warn(
      'Cannot resolve element, will use default positioning ',
      attachToElement,
    );
  }

  return actualAnchorPoint;
}

let x = 0;
let y = 0;
document.addEventListener('mousemove', (ev) => {
  x = ev.clientX;
  y = ev.clientY;
});

export function calculateTooltipPosition({
  anchorPoint,
  attachToElement,
  attachToElementAnchorPoint,
  attachToMousePosition,
  minSize,
  preferredOrientationX,
  preferredOrientationY,
  preferredSize = { height: 'auto', width: 'auto' },
}: Pick<
  TTooltip,
  | 'anchorPoint'
  | 'attachToElement'
  | 'attachToElementAnchorPoint'
  | 'attachToMousePosition'
  | 'minSize'
  | 'preferredOrientationX'
  | 'preferredOrientationY'
  | 'preferredSize'
>): ThemeUICSSObject {
  let actualAnchorPoint = anchorPoint;

  if (attachToMousePosition) {
    actualAnchorPoint = {
      left: x + 3,
      top: y + 3,
    };
  }

  if (attachToElement) {
    actualAnchorPoint = determineAnchorPoint({
      attachToElement,
      attachToElementAnchorPoint,
    });
  }

  if (actualAnchorPoint) {
    const availableBottomSpace = window.innerHeight - actualAnchorPoint.top;
    const availableTopSpace = actualAnchorPoint.top;
    const availableLeftSpace = actualAnchorPoint.left;
    const availableRightSpace = window.innerWidth - actualAnchorPoint.left;

    const isEnoughToRight =
      ((preferredSize?.width !== 'auto' ? preferredSize?.width : undefined) ??
        minSize?.width ??
        0) <= availableRightSpace;
    const isEnoughToLeft =
      ((preferredSize?.width !== 'auto' ? preferredSize?.width : undefined) ??
        minSize?.width ??
        0) <= availableLeftSpace;
    const isEnoughToBottom =
      ((preferredSize?.height !== 'auto' ? preferredSize?.height : undefined) ??
        minSize?.height ??
        0) <= availableBottomSpace;
    const isEnoughToTop =
      ((preferredSize?.height !== 'auto' ? preferredSize?.height : undefined) ??
        minSize?.height ??
        0) <= availableTopSpace;

    const preferredWidth =
      preferredSize.width === 'auto' ? Infinity : preferredSize.width;
    const preferredHeight =
      preferredSize.height === 'auto' ? Infinity : preferredSize.height;

    const position: ThemeUICSSObject = {
      '& > *': {
        width: '100%',
        height: '100%',
        overflow: 'auto',
      },
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      width: preferredSize.width === 'auto' ? 'auto' : undefined,
      height: preferredSize.height === 'auto' ? 'auto' : undefined,
    };

    if (preferredOrientationX === 'left') {
      if (isEnoughToLeft) {
        position.maxWidth = Math.min(preferredWidth, availableLeftSpace);
        position.right = window.innerWidth - actualAnchorPoint.left;
      } else if (isEnoughToRight) {
        position.maxWidth = Math.min(preferredWidth, availableRightSpace);
        position.left = actualAnchorPoint.left;
      } else {
        position.left = 0;
        position.maxWidth = '100vw';
      }
    } else {
      if (isEnoughToRight) {
        position.maxWidth = Math.min(preferredWidth, availableRightSpace);
        position.left = actualAnchorPoint.left;
      } else if (isEnoughToLeft) {
        position.maxWidth = Math.min(preferredWidth, availableLeftSpace);
        position.right = window.innerWidth - actualAnchorPoint.left;
      } else {
        position.left = 0;
        position.maxWidth = '100vw';
      }
    }

    if (preferredOrientationY === 'top') {
      if (isEnoughToTop) {
        position.maxHeight = Math.min(preferredHeight, availableTopSpace);
        position.bottom = window.innerHeight - actualAnchorPoint.top;
      } else if (isEnoughToBottom) {
        position.maxHeight = Math.min(preferredHeight, availableBottomSpace);
        position.top = actualAnchorPoint.top;
      } else {
        position.maxHeight = '100vh';
        position.top = 0;
      }
    } else {
      if (isEnoughToBottom) {
        position.maxHeight = Math.min(preferredHeight, availableBottomSpace);
        position.top = actualAnchorPoint.top;
      } else if (isEnoughToTop) {
        position.maxHeight = Math.min(preferredHeight, availableTopSpace);
        position.bottom = window.innerHeight - actualAnchorPoint.top;
      } else {
        position.maxHeight = '100vh';
        position.top = 0;
      }
    }

    position.minHeight =
      typeof position.maxHeight === 'number'
        ? Math.min(position.maxHeight, minSize?.height ?? 0)
        : (minSize?.height ?? 0);
    position.minWidth =
      typeof position.minWidth === 'number'
        ? Math.min(position.minWidth, minSize?.width ?? 0)
        : (minSize?.width ?? 0);

    return position;
  }

  return { left: 0, top: 0, width: 'auto', height: 'auto' };
}

export function useHandlers({
  attachToElement,
  closeOnClick,
  closeOnClickOut,
  closeOnEscape,
  closeOnMouseLeaveTooltip,
  closeOnMouseLeaveAttachedElement,
  closeOnScrollOut,
  onClose,
}: Pick<
  TTooltip,
  | 'attachToElement'
  | 'closeOnClick'
  | 'closeOnClickOut'
  | 'closeOnEscape'
  | 'closeOnMouseLeaveTooltip'
  | 'closeOnMouseLeaveAttachedElement'
  | 'closeOnScrollOut'
  | 'onClose'
>) {
  const hasInited = useRef(false);
  const close = useCallback(() => {
    onClose?.();
  }, [onClose]);

  useEffect(() => {
    function handleEscape(ev: KeyboardEvent) {
      if (ev.code === 'Escape' && closeOnEscape !== false) {
        close();
      }
    }
    function handleMouseDownOut(ev: MouseEvent) {
      if (!hasInited.current) return;

      const actuallyCloseOnClickOut =
        (closeOnClickOut instanceof Function
          ? closeOnClickOut(ev)
          : closeOnClickOut) === true;

      if (
        actuallyCloseOnClickOut &&
        !isChild(ev.target as HTMLElement, (current) =>
          current.classList?.contains('tooltip__wrapper'),
        )
      ) {
        close();
      }
    }
    function handleScroll(ev: Event) {
      if (!hasInited.current) return;
      if (
        closeOnScrollOut &&
        !isChild(ev.target as HTMLElement, (current) =>
          current.classList?.contains('tooltip__wrapper'),
        )
      ) {
        close();
      }
    }

    function handleMouseLeave(event: MouseEvent) {
      const actuallyCloseOnMouseLeaveTooltip =
        closeOnMouseLeaveTooltip instanceof Function
          ? closeOnMouseLeaveTooltip(event)
          : closeOnMouseLeaveTooltip;

      const actuallyCloseOnMouseLeaveAttachedElement =
        closeOnMouseLeaveAttachedElement instanceof Function
          ? closeOnMouseLeaveAttachedElement(event)
          : closeOnMouseLeaveAttachedElement;

      if (
        !hasInited.current ||
        (!actuallyCloseOnMouseLeaveAttachedElement &&
          !actuallyCloseOnMouseLeaveTooltip)
      )
        return;

      const resolvedAttachToElement = attachToElement
        ? isAttachToElementAFunction(attachToElement)
          ? attachToElement()
          : attachToElement.current
        : null;

      const isTooltipTheTarget = isChild(
        event.target as HTMLElement,
        (current) => current.classList.contains('tooltip__wrapper'),
      );
      const isAttachedTheTarget = isChild(
        event.target as HTMLElement,
        (current) => current === resolvedAttachToElement,
      );

      const isTooltipRelated = isChild(
        event.relatedTarget as HTMLElement,
        (current) => current.classList.contains('tooltip__wrapper'),
      );

      const isAttachedRelated = isChild(
        event.relatedTarget as HTMLElement,
        (current) => current === resolvedAttachToElement,
      );

      if (isTooltipRelated || isAttachedRelated) return;
      if (isAttachedTheTarget && !actuallyCloseOnMouseLeaveAttachedElement)
        return;
      if (isTooltipTheTarget && !actuallyCloseOnMouseLeaveTooltip) return;

      if (isTooltipTheTarget || isAttachedTheTarget) close();
    }

    document.addEventListener('keydown', handleEscape);
    document.addEventListener('mousedown', handleMouseDownOut);
    document.addEventListener('scroll', handleScroll);
    document.addEventListener('mouseout', handleMouseLeave);

    setTimeout(() => {
      hasInited.current = true;
    }, 0);

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('mousedown', handleMouseDownOut);
      document.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mouseout', handleMouseLeave);
    };
  }, [
    attachToElement,
    close,
    closeOnClickOut,
    closeOnEscape,
    closeOnMouseLeaveAttachedElement,
    closeOnMouseLeaveTooltip,
    closeOnScrollOut,
  ]);

  return {
    handleOnClick: useCallback(
      (ev: React.MouseEvent) => {
        ev.stopPropagation();
        if (closeOnClick) {
          close();
        }
      },
      [close, closeOnClick],
    ),
  };
}
